'use client'

import { Stack } from '@mui/material'
import { getToolName } from 'ai'
import { isPlainObject, isString } from 'lodash'
import { useTranslations } from 'next-intl'
import type { ReactNode } from 'react'

import ToolCallPart, { type ToolPart, isToolFailed } from '../parts/ToolCallPart'
import StepLabel, { emphasiseVerb } from './StepLabel'

type ProposalStepRowProps = {
  part: ToolPart
}

/** A call that ended in an approval card, as a muted "Proposed ..." row. The card itself sits in the answer area. */
export default function ProposalStepRow({ part }: ProposalStepRowProps) {
  const t = useTranslations('features.AssistantV2')

  return (
    <Stack spacing={0.5} sx={{ color: 'text.secondary' }}>
      <StepLabel>{proposalLabel(part, t)}</StepLabel>
      {isToolFailed(part) && <ToolCallPart part={part} />}
    </Stack>
  )
}

function proposalLabel(part: ToolPart, t: ReturnType<typeof useTranslations<'features.AssistantV2'>>): ReactNode {
  switch (getToolName(part)) {
    case 'propose_bulk_export':
      return t.rich('proposedBulkExport', { verb: emphasiseVerb })
    case 'propose_merged_report':
      return t.rich('proposedMergedReport', { verb: emphasiseVerb })
    case 'propose_charger_diagnostics':
      return t.rich('proposedDiagnostics', { verb: emphasiseVerb })
    case 'send_email':
      return t.rich('proposedEmail', { verb: emphasiseVerb })
    default: {
      const operationId = operationIdOf(part.input)
      return operationId
        ? t.rich('proposedChangeNamed', { operationId, verb: emphasiseVerb })
        : t.rich('proposedChange', { verb: emphasiseVerb })
    }
  }
}

function operationIdOf(input: unknown): string | undefined {
  if (!isPlainObject(input)) return undefined
  const { operationId } = input as Record<string, unknown>
  return isString(operationId) && operationId ? operationId : undefined
}
